<script setup lang="ts">
import EntityCardShell from '@/components/entity/EntityCardShell.vue';
import EntityActionButton from '@/components/entity/EntityActionButton.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ContextMenuContent, ContextMenuItem, ContextMenuSeparator } from '@/components/ui/context-menu';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { formatShortDate } from '~/lib/formatters';
import type { Proposal } from '~/types/models';
import { Building2, CalendarDays, Copy, Eye, FileText, Mail, MoreHorizontal, Pencil, Send, Share2, Trash2 } from 'lucide-vue-next';

const props = withDefaults(defineProps<{
    proposal: Proposal;
    href: string;
    clientName?: string;
    projectName?: string;
    openLabel?: string;
    compact?: boolean;
    loading?: boolean;
    surface?: 'default' | 'embedded' | 'list';
    canSend?: boolean;
    canCopyPublicLink?: boolean;
    canShareToPortal?: boolean;
    canEdit?: boolean;
    canDelete?: boolean;
}>(), {
    clientName: '',
    projectName: '',
    openLabel: 'Open proposal',
    compact: false,
    loading: false,
    surface: 'list',
    canSend: false,
    canCopyPublicLink: false,
    canShareToPortal: false,
    canEdit: false,
    canDelete: false,
});

const emit = defineEmits<{
    send: [Proposal];
    'copy-public-link': [Proposal];
    'share-to-portal': [Proposal];
    edit: [Proposal];
    delete: [Proposal];
}>();

const formatLabel = computed(() => props.proposal.contentMode === 'uploaded' ? 'Uploaded PDF' : 'Written in Vuey');
const statusVariant = computed(() => props.proposal.status === 'declined' ? 'destructive' : props.proposal.status === 'draft' ? 'outline' : 'secondary');
const subtitle = computed(() => [props.clientName, props.projectName].filter(Boolean).join(' · '));
const updatedLabel = computed(() => formatShortDate(props.proposal.updatedAt || props.proposal.createdAt));
const lifecycleLabel = computed(() => {
    if (props.proposal.status === 'accepted') return `Accepted by client${props.proposal.acceptedAt ? ` · ${formatShortDate(props.proposal.acceptedAt)}` : ''}`;
    if (props.proposal.status === 'declined') return `Declined by client${props.proposal.declinedAt ? ` · ${formatShortDate(props.proposal.declinedAt)}` : ''}`;
    if (props.proposal.status === 'sent') {
        const channel = props.proposal.deliveryMethod === 'email' ? 'Emailed to client' : props.proposal.deliveryMethod === 'portal' ? 'Awaiting portal approval' : 'Public link shared';
        return `${channel}${props.proposal.publicSharedAt ? ` · ${formatShortDate(props.proposal.publicSharedAt)}` : ''}`;
    }
    return 'Not shared with client';
});
</script>

<template>
    <div v-if="loading" class="flex items-center gap-4 px-4 py-5">
        <Skeleton class="size-16 shrink-0 rounded-lg" />
        <div class="min-w-0 flex-1 space-y-2"><Skeleton class="h-4 w-2/5" /><Skeleton class="h-3 w-3/5" /></div>
    </div>
    <EntityCardShell
        v-else
        :href="href"
        :title="proposal.title || 'Untitled proposal'"
        :subtitle="subtitle"
        :fallback-text="proposal.title.slice(0, 2).toUpperCase() || 'PR'"
        :compact="compact"
        :surface="surface"
        :overlay-label="openLabel"
    >
        <template #badges><Badge :variant="statusVariant" class="capitalize">{{ proposal.status }}</Badge></template>
        <template #compact-actions>
            <Badge :variant="statusVariant" class="capitalize">{{ proposal.status }}</Badge>
            <DropdownMenu>
                <DropdownMenuTrigger as-child><Button variant="ghost" size="icon" class="entity-row-action" aria-label="More proposal actions"><MoreHorizontal class="size-4" /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end" class="w-56">
                    <DropdownMenuItem as-child><NuxtLink :to="href"><Eye class="mr-2 size-4" />{{ openLabel }}</NuxtLink></DropdownMenuItem>
                    <DropdownMenuItem v-if="canSend && (proposal.status === 'draft' || proposal.status === 'sent')" @click="emit('send', proposal)"><Mail class="mr-2 size-4" />{{ proposal.status === 'sent' ? 'Send reminder' : 'Send to client' }}</DropdownMenuItem>
                    <DropdownMenuItem v-if="canCopyPublicLink && (proposal.status === 'draft' || proposal.status === 'sent')" @click="emit('copy-public-link', proposal)"><Copy class="mr-2 size-4" />Copy public link</DropdownMenuItem>
                    <DropdownMenuItem v-if="canShareToPortal && proposal.status === 'draft'" @click="emit('share-to-portal', proposal)"><Share2 class="mr-2 size-4" />Share to portal for approval</DropdownMenuItem>
                    <DropdownMenuSeparator v-if="canEdit || canDelete" />
                    <DropdownMenuItem v-if="canEdit && proposal.status === 'draft'" @click="emit('edit', proposal)"><Pencil class="mr-2 size-4" />Edit</DropdownMenuItem>
                    <DropdownMenuItem v-if="canDelete && proposal.status === 'draft'" class="text-destructive focus:text-destructive" @click="emit('delete', proposal)"><Trash2 class="mr-2 size-4" />Delete</DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </template>
        <template #compact-body>
            <p class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span><FileText class="mr-1 inline size-3.5" />{{ formatLabel }}</span>
                <span><Share2 class="mr-1 inline size-3.5" />{{ lifecycleLabel }}</span>
            </p>
        </template>
        <template #mobile-actions>
            <Button variant="outline" size="sm" as-child><NuxtLink :to="href"><Eye class="mr-1.5 size-4" />{{ openLabel }}</NuxtLink></Button>
        </template>
        <template #actions>
            <EntityActionButton :href="href" :label="openLabel" :icon="Eye" />
            <DropdownMenu>
                <DropdownMenuTrigger as-child><Button variant="ghost" size="icon" class="entity-row-action" aria-label="More proposal actions"><MoreHorizontal class="size-4" /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end" class="w-56">
                    <DropdownMenuItem as-child><NuxtLink :to="href"><Eye class="mr-2 size-4" />{{ openLabel }}</NuxtLink></DropdownMenuItem>
                    <DropdownMenuItem v-if="canSend && (proposal.status === 'draft' || proposal.status === 'sent')" @click="emit('send', proposal)"><Mail class="mr-2 size-4" />{{ proposal.status === 'sent' ? 'Send reminder' : 'Send to client' }}</DropdownMenuItem>
                    <DropdownMenuItem v-if="canCopyPublicLink && (proposal.status === 'draft' || proposal.status === 'sent')" @click="emit('copy-public-link', proposal)"><Copy class="mr-2 size-4" />Copy public link</DropdownMenuItem>
                    <DropdownMenuItem v-if="canShareToPortal && proposal.status === 'draft'" @click="emit('share-to-portal', proposal)"><Share2 class="mr-2 size-4" />Share to portal for approval</DropdownMenuItem>
                    <DropdownMenuSeparator v-if="canEdit || canDelete" />
                    <DropdownMenuItem v-if="canEdit && proposal.status === 'draft'" @click="emit('edit', proposal)"><Pencil class="mr-2 size-4" />Edit</DropdownMenuItem>
                    <DropdownMenuItem v-if="canDelete && proposal.status === 'draft'" class="text-destructive focus:text-destructive" @click="emit('delete', proposal)"><Trash2 class="mr-2 size-4" />Delete</DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
            <slot name="actions" />
        </template>
        <template #body>
            <div class="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
                <span class="inline-flex items-center gap-1.5"><FileText class="size-3.5" />{{ formatLabel }}</span>
                <span v-if="clientName" class="inline-flex items-center gap-1.5"><Building2 class="size-3.5" />{{ clientName }}</span>
                <span class="inline-flex items-center gap-1.5"><Share2 class="size-3.5" />{{ lifecycleLabel }}</span>
                <span class="inline-flex items-center gap-1.5"><CalendarDays class="size-3.5" />Updated {{ updatedLabel }}</span>
            </div>
        </template>
        <template #context-menu>
            <ContextMenuContent class="w-56">
                <ContextMenuItem as-child><NuxtLink :to="href"><Eye class="mr-2 size-4" />{{ openLabel }}</NuxtLink></ContextMenuItem>
                <ContextMenuItem v-if="canSend && (proposal.status === 'draft' || proposal.status === 'sent')" @click="emit('send', proposal)"><Mail class="mr-2 size-4" />{{ proposal.status === 'sent' ? 'Send reminder' : 'Send to client' }}</ContextMenuItem>
                <ContextMenuItem v-if="canCopyPublicLink && (proposal.status === 'draft' || proposal.status === 'sent')" @click="emit('copy-public-link', proposal)"><Copy class="mr-2 size-4" />Copy public link</ContextMenuItem>
                <ContextMenuItem v-if="canShareToPortal && proposal.status === 'draft'" @click="emit('share-to-portal', proposal)"><Share2 class="mr-2 size-4" />Share to portal for approval</ContextMenuItem>
                <ContextMenuSeparator v-if="canEdit || canDelete" />
                <ContextMenuItem v-if="canEdit && proposal.status === 'draft'" @click="emit('edit', proposal)"><Pencil class="mr-2 size-4" />Edit</ContextMenuItem>
                <ContextMenuItem v-if="canDelete && proposal.status === 'draft'" class="text-destructive focus:text-destructive" @click="emit('delete', proposal)"><Trash2 class="mr-2 size-4" />Delete</ContextMenuItem>
            </ContextMenuContent>
        </template>
    </EntityCardShell>
</template>
